import { useCallback, useEffect } from 'react'
import { useLocation } from 'react-router'
import { driver, type Config, type Driver, type DriveStep } from 'driver.js'
import 'driver.js/dist/driver.css'
import { ESTILO_TOUR, TOUR_GLOBAL, tourDeRuta, type DefinicionTour } from './pasos'

/**
 * Limpieza defensiva de residuos de driver.js. Mientras un tour está activo,
 * driver pone la clase `driver-active` en <html>, y su CSS aplica
 * `pointer-events:none` a TODA la página salvo el elemento resaltado. Si el
 * tour no se destruye limpiamente (p.ej. se navega a mitad), esa clase se queda
 * pegada y el panel entero deja de responder a clics. Esta función borra la
 * clase y cualquier nodo de overlay/popover huérfano. Es idempotente y barata.
 */
function limpiarResiduos() {
  if (typeof document === 'undefined') return
  const html = document.documentElement
  html.classList.forEach((c) => {
    if (c.startsWith('driver-')) html.classList.remove(c)
  })
  document.querySelectorAll('.driver-overlay, .driver-popover, svg.driver-overlay').forEach((n) => n.remove())
  // driver bloquea el scroll del body mientras corre; restáuralo por si acaso.
  document.body.style.removeProperty('overflow')
}

/** Instancia viva del tour, para poder destruirla al cambiar de ruta o desmontar. */
let activo: Driver | null = null

function construir(def: DefinicionTour) {
  const config: Config = {
    showProgress: true,
    allowClose: true,
    nextBtnText: 'Siguiente',
    prevBtnText: 'Atrás',
    doneBtnText: 'Entendido',
    progressText: '{{current}} de {{total}}',
    popoverClass: `tac-${ESTILO_TOUR}`,
    steps: def.pasos,
    // Para la variante C: pinta el badge «n/total» en el título.
    onPopoverRender: (popover, { config: cfg, state }) => {
      if (ESTILO_TOUR !== 'C') return
      const total = (cfg.steps ?? []).length
      const i = (state.activeIndex ?? 0) + 1
      popover.title?.setAttribute('data-paso', `${i}/${total}`)
    },
    // Al terminar/cerrar, suelta la referencia y limpia por si quedara algo.
    onDestroyed: () => {
      activo = null
      limpiarResiduos()
    },
  }
  return driver(config)
}

function lanzar(def: DefinicionTour) {
  // Nunca dos tours a la vez: cierra el anterior y limpia antes de empezar.
  if (activo) {
    try {
      activo.destroy()
    } catch {
      /* ignora: el instance podría estar ya muerto */
    }
  }
  limpiarResiduos()
  // Filtra los pasos cuyo elemento no está en el DOM ahora mismo (p.ej. el chip
  // de ventana 24h o el compositor de Conversaciones solo existen con un chat
  // abierto). Así el contador «n de total» no cuenta pasos fantasma ni aparecen
  // popovers centrados sin nada que resaltar. Los pasos SIN selector (narración
  // centrada, como el «¡Listo!» del global) se conservan siempre.
  const presentes: DefinicionTour = {
    ...def,
    pasos: def.pasos.filter((paso) => {
      const sel = paso.element
      if (!sel) return true
      if (typeof sel !== 'string') return true
      try {
        return document.querySelector(sel) !== null
      } catch {
        return true
      }
    }),
  }
  const d = construir(presentes.pasos.length ? presentes : def)
  activo = d
  d.drive()
}

/**
 * Controla la guía (driver.js). Expone:
 *  - lanzarGlobal(): recorrido general del panel.
 *  - lanzarDePantalla(): tour específico de la ruta actual (o el global si no hay).
 *  - hayTourDePantalla: si la ruta actual tiene un tour propio.
 *
 * NO hay auto-arranque: el tour solo se abre cuando el usuario pulsa «Guía».
 * (El auto-arranque anterior podía dejar el panel bloqueado si se navegaba a
 * mitad del recorrido — ver limpiarResiduos.) Además, al cambiar de ruta o
 * desmontar, cualquier tour en curso se destruye para no dejar la página muda.
 */
export function useGuia() {
  const { pathname } = useLocation()
  const tourPantalla = tourDeRuta(pathname)

  // Cambiar de ruta corta cualquier tour en curso (el elemento resaltado ya no
  // existe en la pantalla nueva) y garantiza que no quede la página bloqueada.
  useEffect(() => {
    if (activo) {
      try {
        activo.destroy()
      } catch {
        /* noop */
      }
      activo = null
    }
    limpiarResiduos()
  }, [pathname])

  // Red de seguridad: al montar el shell, barre cualquier residuo previo
  // (p.ej. de una versión anterior que dejó `driver-active` pegado).
  useEffect(() => {
    limpiarResiduos()
    return () => {
      if (activo) {
        try {
          activo.destroy()
        } catch {
          /* noop */
        }
        activo = null
      }
      limpiarResiduos()
    }
  }, [])

  const lanzarGlobal = useCallback(() => lanzar(TOUR_GLOBAL), [])
  const lanzarDePantalla = useCallback(() => lanzar(tourPantalla ?? TOUR_GLOBAL), [tourPantalla])

  return {
    lanzarGlobal,
    lanzarDePantalla,
    hayTourDePantalla: tourPantalla !== null,
    tituloTourPantalla: tourPantalla?.titulo ?? null,
  }
}

export type { DriveStep }
